import React, { useEffect } from 'react';
import AppRoutes from '../AppRoutes/AppRoutes';
import FullPageLoader from '@/components/layout/FullPageLoader/FullPageLoader';
import { useSelector, useDispatch } from 'react-redux';
import store, { RootState } from '@/store/store';
import {
  useExtension,
  useFeatureFlag,
  useMixpanel,
  useUser,
  useBraze,
  useAuthStateListener,
} from '@/hooks';
import { IUser } from '@/models';
import { isUserAnonymous } from '@/utils/helpers/user';
import { useSync } from '@/hooks/data/useSync';
import { useGroupsSync } from '@/hooks/data/useGroupsSync';
import {
  PopupActionType,
  setAppReady,
  showPopupMessage,
} from '@/store/reducers';
import { logger, setAppsFlyerCustomerUserId } from '@/utils';
import { LOGGER_FEATURE, LOGGER_OTHER } from '@/constants/logger/logger-tags';
import { IMAGES } from '@/constants/ui/images';
import { selectFlagsConfig } from '@/store/selectors';
import { extensionUtils } from '@/utils/integrations/extension';
import { isClickStreamOptedIn } from '@/utils/helpers/opt-in';
import { showTermsUpdatedModalIfNeeded } from '@/features/dataConsent/TermsUpdatedModal/showTermsUpdatedModal';
import { useConsentAttributesSync } from '@/hooks/config/useConsentAttributesSync.hook';

const AppInit: React.FC = () => {
  const dispatch = useDispatch();
  const appReady = useSelector((state: RootState) => state.appData.appReady);
  const mixpanelHook = useMixpanel();
  const featureFlagHook = useFeatureFlag();
  const userHook = useUser();
  const extensionHook = useExtension();
  const brazeHook = useBraze();
  const syncHook = useSync();

  useConsentAttributesSync();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize Sentry first
        logger.init();

        // Initialize Firebase and wait for anonymous sign-in using the user hook
        const user: IUser = await userHook.init();

        // Check if user is logged in (not anonymous)
        const isLoggedIn = !isUserAnonymous(user);

        // Set user context for Sentry
        if (isLoggedIn) {
          logger.setUser({
            id: user.uid,
            email: user.email || undefined,
            username: user.displayName || undefined,
          });
          setAppsFlyerCustomerUserId(user.uid);
        }

        // load config from remote server
        await syncHook.syncFromRemote();

        // Initialize ExtensionInfo
        await extensionHook.init();
        const extensionInfo = store.getState().extensionInfo;

        // read directly from store because useSelector returns stale data here
        const flagsConfig = selectFlagsConfig(store.getState());

        // Initialize Feature Flag with user attributes for targeting
        await featureFlagHook.initFeatureFlag({
          loggedIn: isLoggedIn,
          privacyOptedIn: isClickStreamOptedIn(flagsConfig),
          installedTime: extensionInfo.installedTime,
        });

        // Initialize Mixpanel
        await mixpanelHook.init(user);

        // Initialize Braze
        await brazeHook.init(user);

        extensionUtils.saveFirstKnownInstalledTime(extensionInfo.installedTime);
        showTermsUpdatedModalIfNeeded(
          flagsConfig,
          user,
          extensionInfo.installedTime,
          featureFlagHook.getFeatureValue,
          dispatch
        );
      } catch (error) {
        if (logger.sentryInitialized) {
          logger.fatal('Failed to initialize app', error, {
            tags: {
              feature: LOGGER_FEATURE.APP_INIT,
              critical: LOGGER_OTHER.CRITICAL,
            },
          });
        }
        dispatch(
          showPopupMessage({
            iconSrc: IMAGES.EXCLAMATION,
            titleKey: 'generalError.title',
            subtitleKey: 'generalError.subtitle',
            buttonTextKey: 'generalError.buttonText',
            buttonAction: { type: PopupActionType.RELOAD },
          })
        );
      } finally {
        dispatch(setAppReady(true));
      }
    };

    initializeApp();
  }, []);

  // Listen to Firebase auth state changes (cross-tab sync)
  useAuthStateListener(appReady);
  useGroupsSync(appReady);

  if (!appReady) return <FullPageLoader />;

  return <AppRoutes />;
};

export default AppInit;
